import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { edgeFunctionErrorMessage } from "@/lib/edge-function-error";
import type { KpiQuote } from "@/lib/kpi-metrics";
import type { AdSpendResponse } from "../../supabase/functions/ad-spend-monthly/spend.ts";

const PAGE_SIZE = 1000;
const KPI_QUOTE_COLUMNS =
  "id, created_at, total, pipeline_stage, is_lost, contact_id, client_email, utm_source, gclid, fbclid, source, square_source";

type KpiClient = Pick<typeof supabase, "from" | "functions">;

/**
 * Every quote, a page at a time. Stops only on an empty page, so a server
 * max_rows below PAGE_SIZE can't silently cut the list short.
 */
export async function fetchAllKpiQuotes(client: KpiClient = supabase): Promise<KpiQuote[]> {
  const all: KpiQuote[] = [];
  let from = 0;
  for (;;) {
    const { data, error } = await client
      .from("quotes")
      .select(KPI_QUOTE_COLUMNS)
      .order("created_at")
      .order("id")
      .range(from, from + PAGE_SIZE - 1);
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as KpiQuote[];
    if (rows.length === 0) return all;
    all.push(...rows);
    from += rows.length;
  }
}

export function useKpiQuotes() {
  return useQuery({
    queryKey: ["kpi_quotes"],
    queryFn: () => fetchAllKpiQuotes(),
    staleTime: 5 * 60_000,
  });
}

export async function fetchAdSpend(client: KpiClient = supabase): Promise<AdSpendResponse> {
  const { data, error } = await client.functions.invoke("ad-spend-monthly", { body: {} });
  // #719: the function's own message is on error.context, not error.message.
  if (error) throw new Error(await edgeFunctionErrorMessage(error));
  return data as AdSpendResponse;
}

// No retry: the function already degrades per platform, and retrying a dead
// token would double the outbound calls and the edge_function_errors rows.
export function useLiveAdSpend() {
  return useQuery({
    queryKey: ["ad-spend-monthly"],
    queryFn: () => fetchAdSpend(),
    staleTime: 15 * 60_000,
    retry: false,
  });
}
